"use client"

import * as React from "react"
import { hasPrebakedVariants } from "@lib/util/image-variants"
import { useTranslations, useLocale } from "next-intl"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { cn } from "@lib/utils"
import Image from "next/image"
import Spinner from "@modules/common/icons/spinner"
import { getLocalizedProductContent } from "@lib/util/product-localization"
import { rentalInfoFromSummary } from "@lib/util/rental-summary"
import { convertToLocale } from "@lib/util/money"
import { getUnitLabel } from "@lib/util/rental"
import type { Locale } from "@/types/i18n"

interface SearchAutocompleteProps {
  products: HttpTypes.StoreProduct[]
  query: string
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  onProductClick: () => void
  selectedIndex: number
  region: HttpTypes.StoreRegion
}

/**
 * SearchAutocomplete component - displays search suggestions dropdown
 * Features:
 * - Localized product titles via metadata translations
 * - Proper price display with rental/sale distinction (matches product cards)
 * - Query highlighting in product titles
 * - Loading state
 */
export function SearchAutocomplete({
  products,
  query,
  isLoading,
  isOpen,
  onClose,
  onProductClick,
  selectedIndex,
  region,
}: SearchAutocompleteProps) {
  const t = useTranslations("common")
  const tRental = useTranslations("product.rental")
  const locale = useLocale() as Locale
  const autocompleteRef = React.useRef<HTMLDivElement>(null)

  const intlLocaleMap: Record<string, string> = {
    ru: "ru-RU",
    en: "en-US",
    cs: "cs-CZ",
    uk: "uk-UA",
  }
  const intlLocale = intlLocaleMap[locale] || "en-US"

  // Close on click outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }
  }, [isOpen, onClose])

  // Scroll selected item into view
  React.useEffect(() => {
    if (selectedIndex >= 0 && autocompleteRef.current) {
      const selectedElement = autocompleteRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        })
      }
    }
  }, [selectedIndex])

  if (!isOpen) {
    return null
  }

  // Highlight matching text in product title
  const highlightQuery = (text: string, query: string) => {
    if (!query) return text

    const parts = text.split(new RegExp(`(${escapeRegex(query)})`, "gi"))
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={index} className="font-semibold text-ui-fg-base">
          {part}
        </strong>
      ) : (
        <span key={index}>{part}</span>
      )
    )
  }

  // Escape special regex characters
  const escapeRegex = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  return (
    <div
      ref={autocompleteRef}
      role="listbox"
      id="search-autocomplete-listbox"
      className="absolute top-full left-0 right-0 mt-2 bg-popover border border-ui-border-base rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto"
    >
      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Spinner className="animate-spin" />
          <span className="ml-2 text-sm text-ui-fg-subtle">
            {t("search.searching")}
          </span>
        </div>
      ) : products.length === 0 ? (
        <div className="p-4 text-center text-ui-fg-subtle">
          <p className="text-sm">{t("search.noResults")}</p>
          <p className="text-xs mt-1">{t("search.tryDifferent")}</p>
        </div>
      ) : (
        <div className="py-2">
          {products.map((product, index) => {
            const imageUrl =
              product.thumbnail ||
              product.images?.[0]?.url ||
              "/placeholder.png"

            // Localized title
            const localizedContent = getLocalizedProductContent(product, locale)

            // Rental info from summary (same as product cards)
            const rentalInfo = rentalInfoFromSummary(product, region.currency_code)

            // Purchase price from variant
            const variant = product.variants?.[0]
            const purchaseAmount = variant?.calculated_price?.calculated_amount
            const currencyCode = variant?.calculated_price?.currency_code || region.currency_code

            const showRental = rentalInfo?.isRentalAvailable &&
              rentalInfo.rentalPrice != null &&
              rentalInfo.rentalUnit

            const showPurchase = !rentalInfo ||
              !rentalInfo.isRentalAvailable ||
              rentalInfo.availabilityMode === "sale_only" ||
              rentalInfo.availabilityMode === "both"

            return (
              <LocalizedClientLink
                key={product.id}
                href={`/products/${product.handle}`}
                onClick={onProductClick}
                data-index={index}
                role="option"
                aria-selected={index === selectedIndex}
                id={`search-option-${index}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 hover:bg-ui-bg-subtle transition-colors",
                  selectedIndex === index && "bg-ui-bg-subtle"
                )}
              >
                <div className="shrink-0 w-12 h-12 relative rounded overflow-hidden bg-ui-bg-subtle">
                  <Image
                    src={imageUrl}
                    alt={localizedContent.title || ""}
                    fill
                    className="object-cover"
                    sizes="48px"
                    // Без нарезанных вариантов ширины не существует: см. lib/util/image-variants.
                    unoptimized={!hasPrebakedVariants(imageUrl)}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    {highlightQuery(localizedContent.title || "", query)}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {/* Rental price */}
                    {showRental && (
                      <span className="text-xs font-medium text-primary">
                        {convertToLocale({
                          amount: rentalInfo.rentalPrice!,
                          currency_code: rentalInfo.currencyCode || currencyCode,
                        })}
                        <span className="text-[10px] text-ui-fg-subtle ml-0.5">
                          / {getUnitLabel(rentalInfo.rentalUnit, 1, locale)}
                        </span>
                      </span>
                    )}
                    {/* Purchase price */}
                    {showPurchase && purchaseAmount != null && (
                      <span className={cn(
                        "text-xs",
                        showRental ? "text-ui-fg-subtle" : "font-medium text-ui-fg-base"
                      )}>
                        {showRental && <span className="mr-0.5">{tRental("buyPrice")}:</span>}
                        {convertToLocale({
                          amount: purchaseAmount / 100,
                          currency_code: currencyCode,
                        })}
                      </span>
                    )}
                    {/* Availability mode badge */}
                    {rentalInfo?.isRentalAvailable && (
                      <span className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                        rentalInfo.availabilityMode === "rental_only"
                          ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                          : "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300"
                      )}>
                        {rentalInfo.availabilityMode === "rental_only"
                          ? tRental("rentalOnly")
                          : tRental("rentOrBuy")}
                      </span>
                    )}
                  </div>
                </div>
              </LocalizedClientLink>
            )
          })}
        </div>
      )}
    </div>
  )
}
